import LogoutIcon from '@mui/icons-material/Logout';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import {
  Box,
  Button,
  Divider,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
  SxProps,
  Theme,
} from '@mui/material';
import { useContext, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import treeCampus from '@/assets/logo-tree-campus.svg';
import treeCitiesWorld from '@/assets/logo-tree-cities-world.svg';
import treeCityUSA from '@/assets/logo-tree-city-usa.svg';
import treeLine from '@/assets/logo-tree-line.svg';
import { SelectedPartnerContext } from '@/Contexts/SelectedPartnerContext';
import { UsersContext } from '@/Contexts/UsersContext';
import useAuth from '@/hooks/useAuth';
import { PartnerProps } from '@/models/Partner';
import { CampusProgramIds, programIdToProgramName } from '@/models/Program';
import { UserType } from '@/models/User';
import { SelectedProgramContext } from '@/Contexts/SelectedProgramContext';
import useUser from '@/hooks/useUser';
import { LocaleContext } from '@/Contexts/LocaleContext';

const programIdToLogo: Record<number, string> = {
  1: treeCityUSA,
  2: treeCitiesWorld,
  3: treeLine,
  4: treeCampus,
  5: treeCampus,
  6: treeCampus,
};

interface TruncatedTextProps {
  text?: string;
  variant?: 't4' | 't6' | 'subtitle1' | 'subtitle2' | 'subtitle3';
  sx?: SxProps<Theme>;
  fontWeight?: string | number;
}

function TruncatedWithTooltip({ text, variant, sx, fontWeight }: TruncatedTextProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    if (ref.current != null) {
      setIsOverflowing(ref.current.scrollWidth > ref.current.clientWidth);
    }
  }, [text]);

  return (
    <Tooltip title={isOverflowing ? text : ''}>
      <Typography
        ref={ref}
        variant={variant}
        fontWeight={fontWeight}
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          ...sx,
        }}
      >
        {text}
      </Typography>
    </Tooltip>
  );
}

interface ProfileMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

function ProfileMenu({ anchorEl, open, onClose }: ProfileMenuProps) {
  const navigate = useNavigate();
  const { userCurrent } = useContext(UsersContext);
  const { partnersInPartnerGroup, partnerId, partnerInfo } = useContext(SelectedPartnerContext);
  const { selectedProgramId } = useContext(SelectedProgramContext);
  const { logoutCurrentUser } = useAuth();
  const theme = useTheme();
  const { updateCurrentPartnerId } = useUser();
  const { translatedText } = useContext(LocaleContext);

  const menuItemSx = {
    py: 1.5,
    px: 2,
    display: 'flex',
    alignItems: 'center',
    height: 64,
    lineHeight: 1.3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    // create dividers between menu items using box shadow
    '&:not(:last-child)': {
      boxShadow: `0px 1.5px 0px 0px ${theme.palette.sandShade4.main}`,
    },
  };

  const handleManageAccount = () => {
    onClose();
    navigate('/myaccount');
  };

  const handlePartnerSelect = async (partner: PartnerProps) => {
    if (partner.id == null) {
      onClose();
      return;
    }

    if (userCurrent.currentPartnerId !== partner.id) {
      await updateCurrentPartnerId(partner.id);
    }

    onClose();
    navigate('/');
  };

  const handleAddAccount = () => {
    onClose();
    navigate('/addaccount');
  };

  const handleSignOut = async () => {
    onClose();
    await logoutCurrentUser('UserSignOut');
  };

  const navigateToAdmin = () => {
    onClose();
    navigate('/admin');
  };

  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && scrollRef.current != null) {
      scrollRef.current.scrollTop = 0;
      setShowStickyHeader(false);
    }
  }, [open]);

  const handleScroll = () => {
    if (scrollRef.current != null) {
      setShowStickyHeader(scrollRef.current.scrollTop > 50);
    }
  };

  const currentLogo =
    partnerInfo?.programId != null ? (programIdToLogo[partnerInfo.programId] ?? null) : null;

  const partnerName = partnerInfo?.partnerName ?? userCurrent?.contact?.firstName ?? '';

  const programNameBadge = (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box
        sx={{
          mt: 1,
          display: 'flex',
          borderRadius: '999px',
          px: 1.5,
          py: 0.75,
          backgroundColor: theme.palette.informationBackground.main,

          textAlign: 'center',
          width: 'fit-content',
        }}
      >
        <Typography
          variant='subtitle3'
          sx={{
            fontSize: '0.75rem',
            lineHeight: 1.2,
          }}
        >
          {programIdToProgramName[selectedProgramId]}
        </Typography>
      </Box>
    </Box>
  );

  const partnerMenuItems = (partner: PartnerProps) => (
    <MenuItem
      key={partner.id}
      onClick={() => handlePartnerSelect(partner)}
      sx={{
        ...menuItemSx,
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <AccountCircleIcon fontSize='large' />
      </Box>
      <Box
        sx={{
          overflow: 'hidden',
          maxWidth: '100%',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          flexDirection: 'column',
        }}
      >
        <TruncatedWithTooltip text={partner.partnerName!} variant='subtitle1' fontWeight='bold' />
        {partner.programId != null && (
          <Typography variant='subtitle3' color='text.secondary'>
            {programIdToProgramName[partner.programId]}
          </Typography>
        )}
      </Box>
    </MenuItem>
  );

  const signOutMenuItem = (
    <MenuItem onClick={handleSignOut} sx={{ ...menuItemSx, gap: 1 }}>
      <LogoutIcon fontSize='medium' />
      <Typography variant='subtitle1' sx={{ letterSpacing: 0.5, fontWeight: 'bold' }}>
        {translatedText.profileMenu.signOut}
      </Typography>
    </MenuItem>
  );

  const allowAdminAccess = userCurrent?.userType === UserType.Adf;

  const adminMenuItem = (
    <MenuItem onClick={navigateToAdmin} sx={{ ...menuItemSx, gap: 1 }}>
      <AdminPanelSettingsIcon fontSize='medium' />
      <Typography variant='subtitle1' sx={{ letterSpacing: 0.5, fontWeight: 'bold' }}>
        {translatedText.profileMenu.admin}
      </Typography>
    </MenuItem>
  );

  const allowAccountManagement =
    userCurrent?.userType === UserType.Applicant &&
    userCurrent.contactId !== null &&
    userCurrent.partnerGroupId !== null;

  const AddAnotherAccountMenuItem = (
    <MenuItem onClick={handleAddAccount} sx={{ ...menuItemSx, gap: 1 }}>
      <AddIcon fontSize='medium' />
      <Typography variant='subtitle1' sx={{ letterSpacing: 0.5, fontWeight: 'bold' }}>
        {translatedText.profileMenu.addAccount}
      </Typography>
      <Box sx={{ float: 'right', marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
        <Tooltip title={translatedText.profileMenu.addAccountTooltip}>
          <InfoOutlineIcon fontSize='medium' color='taiga' sx={{ alignItems: 'center' }} />
        </Tooltip>
      </Box>
    </MenuItem>
  );

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            width: 320,
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1,
            backgroundColor: theme.palette.sand.main,
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 20,
              width: 10,
              height: 10,
              backgroundColor: theme.palette.sand.main,
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{ maxHeight: 'calc(100vh - 120px)', overflowY: 'auto', overflowX: 'hidden' }}
      >
        {/* Zero-height sticky anchor — takes no layout space */}
        <Box sx={{ position: 'sticky', top: 0, height: 0, zIndex: 1 }}>
          {/* Compact header slides in/out absolutely from the anchor */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              backgroundColor: theme.palette.sand.main,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              px: 2,
              py: 1,
              borderBottom: `1px solid ${theme.palette.sandShade4.main}`,
              opacity: showStickyHeader ? 1 : 0,
              transform: showStickyHeader ? 'translateY(0)' : 'translateY(-100%)',
              pointerEvents: showStickyHeader ? 'auto' : 'none',
              transition: 'opacity 0.2s ease, transform 0.2s ease',
            }}
          >
            <Box
              display={'flex'}
              flexDirection={'row'}
              alignItems={'center'}
              justifyContent={'center'}
              gap={1}
            >
              <Typography variant='t4' fontWeight='bold'>
                {partnerName}
              </Typography>
            </Box>
            <Box sx={{ overflow: 'hidden', maxWidth: '100%' }}>
              <TruncatedWithTooltip text={userCurrent?.email ?? ''} variant='subtitle3' />
            </Box>
          </Box>
        </Box>

        {/* Full header — scrolls away */}
        <Box sx={{ p: '8px', textAlign: 'center' }}>
          <Typography variant='t4' letterSpacing={1}>
            {partnerName}
          </Typography>
          <TruncatedWithTooltip text={userCurrent?.email ?? ''} variant='subtitle3' />
          {currentLogo != null && (
            <Box
              key={currentLogo}
              sx={{
                my: 1.5,
                alignItems: 'center',
                justifyContent: 'center',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <img
                style={{ cursor: 'pointer' }}
                height='60px'
                src={currentLogo}
                alt={'Program Logo'}
              />
              {CampusProgramIds.includes(selectedProgramId) && programNameBadge}
            </Box>
          )}

          {allowAccountManagement && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant='secondary'
                size='small'
                onClick={handleManageAccount}
                sx={{
                  mt: 0.5,
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                  alignSelf: 'center',
                }}
              >
                {translatedText.profileMenu.manageAccount}
              </Button>
            </Box>
          )}
        </Box>

        <Divider />

        <Box sx={{ margin: 0.5, backgroundColor: theme.palette.sandTint.main }}>
          {/* Partner list */}
          {partnersInPartnerGroup != null &&
            partnersInPartnerGroup.length > 1 &&
            partnersInPartnerGroup
              .filter((partner) => partner.id !== partnerId)
              .map((partner) => partnerMenuItems(partner))}

          {allowAccountManagement && AddAnotherAccountMenuItem}
          {allowAdminAccess && adminMenuItem}
          {signOutMenuItem}
        </Box>
      </Box>
    </Menu>
  );
}

export default ProfileMenu;
